import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import api from '../lib/api';
import { trackFunnelEvent } from '../lib/funnel-tracker';
import { ArrowRight, Sparkles, Loader2, Clock, BookOpenCheck, Target } from 'lucide-react';
import { toast } from 'sonner';

/**
 * QuickOnboarding — Wizard de 3 preguntas → recomendación automática → CTA grande
 * "Comenzar ahora" que entra directo al primer módulo (Borradores).
 *
 * Diseñado para responder el agujero del funnel: 32 sesiones → 2 estrategias → 0 módulos.
 * Tracking granular en cada paso (trackFunnelEvent).
 */

const QUESTIONS = [
    {
        id: 'tiempo',
        stage: 'onboarding_q1_answered',
        icon: Clock,
        title: '¿Cuánto tiempo tienes disponible a la semana?',
        subtitle: 'Esto nos ayuda a recomendarte una ruta sostenible.',
        options: [
            { value: 'poco', label: 'Menos de 3 horas semanales', hint: 'Prefiero avanzar poco a poco' },
            { value: 'regular', label: '3 a 8 horas semanales', hint: 'Puedo dedicarle tiempo regular' },
            { value: 'harto', label: 'Más de 8 horas semanales', hint: 'Quiero involucrarme a fondo' },
        ],
    },
    {
        id: 'experiencia',
        stage: 'onboarding_q2_answered',
        icon: BookOpenCheck,
        title: '¿Es tu primera evaluación docente?',
        subtitle: 'Calibramos el acompañamiento según tu experiencia.',
        options: [
            { value: 'primera', label: 'Sí, es mi primera vez', hint: 'Necesito orientación completa' },
            { value: 'segunda', label: 'Es mi segunda evaluación', hint: 'Ya tengo nociones' },
            { value: 'experimentada', label: 'Tres o más evaluaciones', hint: 'Quiero optimizar mi tiempo' },
        ],
    },
    {
        id: 'motivacion',
        stage: 'onboarding_q3_answered',
        icon: Target,
        title: '¿Qué te motiva más en este proceso?',
        subtitle: 'Tu objetivo define la ruta estratégica que te conviene.',
        options: [
            { value: 'aprobar', label: 'Asegurar un buen resultado', hint: 'Quiero llegar a Competente con tranquilidad' },
            { value: 'destacar', label: 'Destacar en mi desempeño', hint: 'Apunto a Destacado en lo que pueda' },
            { value: 'crecer', label: 'Aprender en el proceso', hint: 'Quiero crecer profesionalmente' },
        ],
    },
];

// Mapeo respuestas → estrategia recomendada del motor estratégico
function recommendStrategy({ tiempo, experiencia, motivacion }) {
    if (tiempo === 'poco') {
        return { id: 'ruta_inteligente', label: 'Ruta Inteligente', razon: 'Pocas horas semanales: máximo retorno con esfuerzo focalizado en un solo Destacado tractor (Formulación de objetivos).' };
    }
    if (motivacion === 'destacar' && tiempo === 'harto') {
        return { id: 'alto_rendimiento', label: 'Alto Rendimiento', razon: 'Tienes el tiempo y la ambición: vamos por la máxima cantidad de Destacados sin sobreexigir indicadores extremos.' };
    }
    if (motivacion === 'aprobar') {
        return { id: 'competente_integral', label: 'Competente Integral', razon: 'Apuntamos a Competente sólido en todos los indicadores, sin presión de Destacados.' };
    }
    if (experiencia === 'primera' || motivacion === 'crecer') {
        return { id: 'recomendacion_acontista', label: 'Recomendación Acontista Educa', razon: 'Equilibrio entre aprendizaje profesional y resultado: tractores estructurales + 4 Destacados clave.' };
    }
    return { id: 'destacado_focalizado', label: 'Destacado Focalizado', razon: 'Destacados en los indicadores de mayor retorno y Competente sólido en los más exigentes.' };
}

export default function QuickOnboarding() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [step, setStep] = useState(0); // 0..2 preguntas; 3 = recomendación
    const [answers, setAnswers] = useState({});
    const [saving, setSaving] = useState(false);
    const [recomendacion, setRecomendacion] = useState(null);

    useEffect(() => {
        trackFunnelEvent('onboarding_started', { user_id: user?.id });
    }, [user?.id]);

    const handleAnswer = (qIdx, value) => {
        const q = QUESTIONS[qIdx];
        const next = { ...answers, [q.id]: value };
        setAnswers(next);
        trackFunnelEvent(q.stage, { user_id: user?.id, meta: { [q.id]: value } });

        if (qIdx < QUESTIONS.length - 1) {
            setStep(qIdx + 1);
        } else {
            const rec = recommendStrategy(next);
            setRecomendacion(rec);
            setStep(QUESTIONS.length);
            trackFunnelEvent('strategy_recommended', {
                user_id: user?.id,
                meta: { ...next, estrategia: rec.id },
            });
        }
    };

    const confirmAndStart = async () => {
        if (!recomendacion) return;
        setSaving(true);
        try {
            // Persistir estrategia en backend (best-effort, no bloquea si falla)
            try {
                await api.put('/profile', {
                    perfil: { estrategia_activa: recomendacion.id, onboarding_completado: true },
                });
            } catch (_) {
                /* el flujo no debe bloquearse por un fallo de profile */
            }
            trackFunnelEvent('strategy_confirmed', {
                user_id: user?.id, meta: { estrategia: recomendacion.id },
            });
            trackFunnelEvent('first_module_opened', { user_id: user?.id, last_action: 'cta_comenzar_ahora' });
            toast.success('¡Listo! Empezamos con tu Módulo 1.');
            navigate('/borradores');
        } catch (err) {
            toast.error('No pudimos guardar tu estrategia, pero igual te llevamos al módulo.');
            navigate('/borradores');
        }
    };

    // Skip total (escape hatch)
    const skipOnboarding = () => {
        trackFunnelEvent('abandoned', { user_id: user?.id, last_action: 'skip_onboarding', meta: { step } });
        navigate('/dashboard');
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50/40 px-4 py-12">
            <div className="max-w-2xl mx-auto" data-testid="quick-onboarding">
                {/* Progress bar */}
                <div className="mb-8">
                    <div className="flex justify-between items-center mb-2">
                        <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                            Onboarding · Paso {Math.min(step + 1, QUESTIONS.length + 1)} de {QUESTIONS.length + 1}
                        </p>
                        <button
                            type="button"
                            onClick={skipOnboarding}
                            className="text-xs text-slate-400 hover:text-slate-700 underline"
                            data-testid="onboarding-skip-btn"
                        >
                            Saltar por ahora
                        </button>
                    </div>
                    <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-amber-600 transition-all duration-500"
                            style={{ width: `${((step + 1) / (QUESTIONS.length + 1)) * 100}%` }}
                        />
                    </div>
                </div>

                {step < QUESTIONS.length ? (
                    <Card className="p-8 border-slate-200 shadow-sm" data-testid={`onboarding-step-${step}`}>
                        {(() => {
                            const q = QUESTIONS[step];
                            const Icon = q.icon;
                            return (
                                <>
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="h-10 w-10 rounded-xl bg-slate-900 text-white inline-flex items-center justify-center">
                                            <Icon className="h-5 w-5" />
                                        </div>
                                        <h2 className="text-2xl font-bold text-slate-900 leading-tight">{q.title}</h2>
                                    </div>
                                    <p className="text-sm text-slate-500 mb-6 ml-13">{q.subtitle}</p>
                                    <div className="space-y-3">
                                        {q.options.map((opt) => (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                onClick={() => handleAnswer(step, opt.value)}
                                                className="w-full text-left p-4 rounded-xl border-2 border-slate-200 hover:border-amber-700 hover:bg-amber-50/50 transition-all group"
                                                data-testid={`onboarding-option-${q.id}-${opt.value}`}
                                            >
                                                <p className="font-semibold text-slate-900 group-hover:text-amber-900">{opt.label}</p>
                                                <p className="text-xs text-slate-500 mt-1">{opt.hint}</p>
                                            </button>
                                        ))}
                                    </div>
                                </>
                            );
                        })()}
                    </Card>
                ) : (
                    /* Recomendación final */
                    <Card
                        className="p-8 border-amber-300 shadow-md bg-gradient-to-br from-amber-50/80 via-white to-white"
                        data-testid="onboarding-recommendation"
                    >
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="h-5 w-5 text-amber-700" />
                            <p className="text-xs uppercase tracking-wide text-amber-700 font-semibold">
                                Tu ruta estratégica recomendada
                            </p>
                        </div>
                        <h2 className="text-3xl font-bold text-slate-900 leading-tight mb-3">
                            {recomendacion.label}
                        </h2>
                        <p className="text-base text-slate-700 leading-relaxed mb-6">{recomendacion.razon}</p>

                        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">¿Qué sigue?</p>
                            <ol className="space-y-2 text-sm text-slate-700">
                                <li className="flex gap-2"><span className="font-bold text-amber-700">1.</span> Entras a tu primer módulo (Borradores) con la rúbrica oficial 2026.</li>
                                <li className="flex gap-2"><span className="font-bold text-amber-700">2.</span> Trabajas el Indicador 1.1 (Formulación de objetivos), el más estratégico.</li>
                                <li className="flex gap-2"><span className="font-bold text-amber-700">3.</span> Tu progreso queda guardado automáticamente.</li>
                            </ol>
                        </div>

                        <Button
                            size="lg"
                            onClick={confirmAndStart}
                            disabled={saving}
                            className="w-full bg-amber-700 hover:bg-amber-800 text-white text-base font-semibold py-6 rounded-xl shadow-sm group"
                            data-testid="onboarding-comenzar-ahora-btn"
                        >
                            {saving ? (
                                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                            ) : (
                                <>
                                    Comenzar ahora
                                    <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
                                </>
                            )}
                        </Button>
                        <button
                            type="button"
                            onClick={() => { setStep(0); setAnswers({}); setRecomendacion(null); }}
                            className="w-full mt-3 text-xs text-slate-500 hover:text-slate-800 underline"
                            data-testid="onboarding-retry-btn"
                        >
                            Volver a responder
                        </button>
                    </Card>
                )}
            </div>
        </div>
    );
}
